import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    mat3,
    mat4,
    quat,
    vec2,
    vec3,
} from 'gl-matrix';
import { isPatientAligned } from '~/domain/viewport/frameRotation';
import {
    computeViewOrientation,
    Orientations,
    standardViewOrientation,
    standardViewOrientations,
    type OrientationDictionnary,
    type ViewOrientationGrid,
} from '../viewOrientation';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import type { SliceAxis } from '~/engine/data/common/Grid';
import { RotatedBoundingBox } from '~/engine/data/common/RotatedBoundingBox';
import { ColorMask } from '~/engine/util/ColorMask';
import { Environment } from '../../data/Environment';
import { PointOfInterest } from '../../data/PointOfInterest';
import { BoundingBox } from '../../data/common/BoundingBox';
import { projectPatientToCanvasPx } from '../../util/geometry/camera';
import { Rectangle3D } from '../../util/geometry/Rectangle3D';
import { PanZoom } from '../PanZoom';
import type { ViewGeometryLink } from '../ViewGeometryLink';
import { FrameCamera, TextureView } from '../TextureView';
import { ContourLayer } from '../layers/ContourLayer';
import { DoseLayer } from '../layers/DoseLayer';
import { ImageLayer } from '../layers/ImageLayer';
import { LayerBit } from '../layers/LayerBit';
import { LayerClass } from '../layers/Layer';
import { Observer } from '~/engine/core/observable';

export type MouseData = {
    patientCoordinates?: ReadonlyVec3; //patient space coordinates
    imageCoordinates?: Map<number, ReadonlyVec3>; //scan id, pixel coordinates
    dataValues?: Map<number, number>; //data source, value
    contours?: Map<number, number[]>; //structureSet ID, roi numbers of nearby structures
};

export { Orientations };

/**
 * Stable identity of one view surface (a viewport slot), unique among the
 * views registered on one ViewManager and fixed for the surface's lifetime.
 * Unlike {@link PlaneView.label} it never changes after construction.
 */
export type ViewId = string & { readonly __viewId: unique symbol };

export function toViewId(value: string): ViewId {
    if (value === '') {
        throw new Error('ViewId must be a non-empty string.');
    }
    return value as ViewId;
}

export const defaultMinimumZoomLevel = 180.0; // Minimum field of view in mm (18cm)

/**
 * {@link PlaneView} is a {@link TextureView} that visualizes 3D data from the perspective of a plane.
 */
export class PlaneView extends TextureView {
    /**
     * Whether two views are the same view surface. Object identity, with a
     * structural fallback (same live Environment, same {@link viewId}, same
     * current orientation) so a surface remounted mid-session — same slot,
     * same dataset, same plane — still matches its pre-remount instance.
     * The environment leg compares object references: a re-minted
     * Environment of the same dataset never matches, so stale captures fail
     * closed. The orientation leg is read live because {@link label} is
     * mutable; a slot remounted onto a different plane does not match.
     */
    public static isSameSurface(a: PlaneView, b: PlaneView): boolean {
        return (
            a === b ||
            (a.environment === b.environment &&
                a.viewId === b.viewId &&
                a.label === b.label)
        );
    }

    public override get isPlaneView(): boolean {
        return true;
    }

    public get planeIntersectionPoint(): PointOfInterest {
        return this.environment.planeIntersectionPoint;
    }

    public get panZoom(): PanZoom {
        return this.environment.panZoom;
    }
    public suppressNextClick(): void {
        this._suppressNextClick = true;
    }

    public consumeClickSuppression(): boolean {
        if (!this._suppressNextClick) {
            return false;
        }
        this._suppressNextClick = false;
        return true;
    }

    private listener?: PlaneViewMouseListener;

    private panZoomObserver?: Observer<number>;
    private planeIntersectionObserver?: Observer<ReadonlyVec3>;

    private _suppressNextClick = false;

    /** Whether the change under way is a pan or zoom the user makes here. */
    private movedByUser = false;

    /** The link this view shares its scale and centre through, while in one. */
    public geometryLink: ViewGeometryLink | undefined;

    /**
     * Method to get the default background_color used by
     */
    public static get PLANEVIEW_DEFAULT_BACKGROUND_COLOR(): ColorMask {
        return new ColorMask(0.1, 0.1, 0.1);
    }

    /** The orthographic view → NDC projection of every plane view; the displayed plane is NDC z = 0. */
    public static readonly ORTHOGRAPHIC_PROJECTION: ReadonlyMat4 =
        mat4.fromValues(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, -2, 0, 0, 0, 0, 1);

    /**
     * Orientation of the default transversal view.
     * Transformation from ViewSpace to PatientSpace
     * Viewspace X is along Worldspace X,
     * Viewspace Y is inverted along Worldspace Y,
     * Viewspace Z is inverted along Worldspace Z.
     */
    public static get TRANSVERSAL_ORIENTATION(): mat3 {
        return standardViewOrientation(Orientations.TRANSVERSAL_ORIENTATION);
    }

    public static get TRANSVERSAL_ORIENTATION_INVERTED(): mat3 {
        return mat3.fromValues(-1.0, 0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, -1.0);
    }

    /**
     * Orientation of the default coronal (frontal) view.
     * Transformation from ViewSpace to PatientSpace
     * Viewspace X is along Worldspace X,
     * Viewspace Y is along Worldspace Z,
     * Viewspace Z is inverted along Worldspace Y.
     */
    public static get CORONAL_ORIENTATION(): mat3 {
        return standardViewOrientation(Orientations.CORONAL_ORIENTATION);
    }

    public static get CORONAL_ORIENTATION_INVERTED(): mat3 {
        return mat3.fromValues(-1.0, 0.0, 0.0, 0.0, 0.0, 1.0, 0.0, 1.0, 0.0);
    }

    /**
     * Orientation of the default sagittal view.
     * Transformation from ViewSpace to PatientSpace
     * Viewspace X is along Worldspace Y,
     * Viewspace Y is along Worldspace Z,
     * Viewspace Z is inverted along Worldspace X.
     */
    public static get SAGITTAL_ORIENTATION(): mat3 {
        return standardViewOrientation(Orientations.SAGITTAL_ORIENTATION);
    }

    public static get SAGITTAL_ORIENTATION_INVERTED(): mat3 {
        return mat3.fromValues(0.0, 1.0, 0.0, 0.0, 0.0, 1.0, 1.0, 0.0, 0.0);
    }

    /**
     * An {@link OrientationDictionary} object mapping {@link Orientations} enum keys to their corresponding {@link mat3} transformation matrices.
     */
    public static get ORIENTATIONS(): OrientationDictionnary {
        return standardViewOrientations();
    }

    /**
     * The view-to-patient rotation of the `view` anatomical plane on an
     * image: {@link computeViewOrientation}, kept on the class as the name
     * its callers know.
     */
    public static computeViewOrientation(
        imageGrid: ViewOrientationGrid,
        view: Orientations
    ): mat3 {
        return computeViewOrientation(imageGrid, view);
    }

    /**
     * The orientation of this {@link PlaneView} relative to PatientSpace.\
     * This is the {@link rotation} from ViewSpace to PatientSpace when {@link additionalRotation} is either undefined or identity.\
     */
    private readonly orientation: mat3;

    /**
     * Changes this view's orientation, keeping the plane intersection
     * point (the pivot the crosshair and the oblique rotate tool both
     * spin about) fixed at the same screen position.
     *
     * Without this, only the *depth* along the new normal gets corrected
     * (via `updateToOrthoPosition`, triggered below): `_position`'s
     * in-plane (view-local X/Y) components are left as-is, which
     * reinterprets them in the NEW, rotated basis as if they were still
     * valid there. They are not the same physical offset, so the pivot
     * visually drifts across the screen on every reorientation instead of
     * holding still -- the effective centre ends up looking like the
     * middle of the image.
     *
     * The fix: rotate `_position`'s offset from the pivot by the same
     * delta being applied to the basis. For a rotation matrix, transpose
     * is inverse, so `Rdelta = newRotation * oldRotation^T` is the
     * rotation that carries the OLD basis to the NEW one; applying it to
     * `_position - pivot` keeps that offset's coordinates identical when
     * later re-expressed in the new basis (the two effects cancel: see
     * the derivation in the task-8 report). `oldRotation` must be cloned
     * *before* `this.orientation` is overwritten below -- `this.rotation`
     * returns `this.orientation` by reference (not a copy) whenever
     * `additionalRotation` is unset, so reading it after the copy would
     * silently produce identity for the delta instead of the actual
     * change.
     */
    public setOrientation(
        newOrientationMatrix: ReadonlyMat3,
        newOrientationLabel?: Orientations
    ): void {
        if (newOrientationLabel) {
            this.label = newOrientationLabel;
        }

        const oldRotation = mat3.clone(this.rotation);
        mat3.copy(this.orientation, newOrientationMatrix);
        const newRotation = this.rotation;

        const pivot = this.environment.planeIntersectionPoint.coordinates;
        const rotationDelta = mat3.multiply(
            mat3.create(),
            newRotation,
            mat3.transpose(mat3.create(), oldRotation)
        );
        const positionRelativeToPivot = vec3.subtract(
            vec3.create(),
            this._position,
            pivot
        );
        vec3.transformMat3(
            positionRelativeToPivot,
            positionRelativeToPivot,
            rotationDelta
        );
        vec3.add(this._position, pivot, positionRelativeToPivot);

        this.updateViewMatrix();
        this.updateView();
        this.updatePlaneIntersectionPoint(
            this.environment.planeIntersectionPoint.coordinates
        );
    }

    /**
     * The orientation of the `view` plane with no frame rotation applied:
     * derived from the image grid when an image is loaded, the standard
     * patient axes otherwise.
     */
    private baseOrientation(view: Orientations): mat3 {
        const imageGrid = this.environment.image?.grid;
        return imageGrid
            ? PlaneView.computeViewOrientation(imageGrid, view)
            : PlaneView.ORIENTATIONS[view];
    }

    /**
     * Orients this view as the `viewType` plane turned by `frameRotation`, a
     * PatientSpace rotation, so it left-multiplies the view-to-patient base.
     */
    public applyFrameRotation(
        frameRotation: ReadonlyMat3,
        viewType: Orientations
    ): void {
        const base = this.baseOrientation(viewType);
        this.setOrientation(
            mat3.multiply(mat3.create(), frameRotation, base),
            viewType
        );
    }

    public getOrientation(): mat3 {
        return this.orientation;
    }

    /**
     * True when this view sits on its standard anatomical plane.
     *
     * `label` is only a NAME, set by whoever called `setOrientation`; it
     * stays TRANSVERSAL however the matrix is actually oriented. Anything
     * whose correctness depends on the view really being axial must ask
     * this, not the label. Whether the displayed plane coincides with an
     * image slice plane is a different question, answered by
     * {@link imageSliceAxis}.
     */
    public isPatientAligned(): boolean {
        const base = this.baseOrientation(
            this.label ?? Orientations.TRANSVERSAL_ORIENTATION
        );
        // The frame rotation is whatever takes the base orientation to the
        // current one: R = current * base^-1. For a rotation matrix the
        // inverse is the transpose.
        const baseInv = mat3.transpose(mat3.create(), base);
        const frame = mat3.multiply(mat3.create(), this.orientation, baseInv);
        return isPatientAligned(frame);
    }

    /**
     * Optional {@link mat3 RotationMatrix} that is applied on top of {@link orientation} to define the complete {@link rotation} of this {@link PlaneView}.\
     */
    private readonly additionalRotation?: mat3;

    private _projectionViewMatrix: mat4;

    /**
     * Transformation from PatientSpace to ViewSpace.\
     * {@link _viewMatrix viewMatrix} is the inverse of {@link _transformationMatrix transformationMatrix}.
     */
    private _viewMatrix: mat4;

    /**
     * Transformation from ViewSpace to PatientSpace.\
     * {@link _transformationMatrix transformationMatrix} is the inverse of {@link _viewMatrix viewMatrix}.
     */
    protected _transformationMatrix: mat4;

    /**
     * {@link _projectionMatrix ProjectionMatrix} is a {@link mat4 matrix} that gives points orthographic projection.\
     * It has the value of:\
     * {1, 0, 0, 0,\
     * 0, 1, 0, 0,\
     * 0, 0, -2, 0,\
     * 0, 0, 0, 1}
     */
    private _projectionMatrix: mat4;

    /**
     * PatientSpace coordinates of the {@link PlaneView}'s center point. Defines the focal point of the {@link PlaneView}.\
     */
    protected _position: vec3;

    /**
     * sizeMm from middle to any of the edges.
     * So basically half of the size from left to right or top to bottom.
     * This is the format used in matrices
     */
    protected _scale: vec2 = vec2.fromValues(1, 1);

    /**
     * Constructor to create a new {@link PlaneView}.\
     * {@link PlaneView}s always require orientation, which defines the rotation of the PlaneView in PatientSpace.\
     * {@link PlaneView}s may also be provided a {@link HTMLCanvasElement} during the construction, that the {@link PlaneView} will use as it's output.
     * @param orientation The {@link orientation} of this {@link PlaneView} in PatientSpace.\
     * The {@link rotation} of the {@link PlaneView} may also be altered by modifying its {@link additionalRotation}.
     * @param canvas Optional canvas that this {@link PlaneView} will use as its own.
     */
    constructor(
        env: Environment,
        private readonly _eventTarget: EventTarget,
        orientation: mat3,
        canvas: HTMLCanvasElement,
        canvas2D: HTMLCanvasElement,
        public readonly viewId: ViewId,
        public label?: Orientations
    ) {
        super(env, canvas, canvas2D);
        this.orientation = orientation;
        this._projectionMatrix = mat4.clone(PlaneView.ORTHOGRAPHIC_PROJECTION);
        this._position = vec3.create();
        this._transformationMatrix = mat4.create();
        this._viewMatrix = mat4.create();
        this._projectionViewMatrix = mat4.create();
        this.updateProjectionViewMatrix();

        this.panZoomObserver = (value: number) => {
            if (value !== this._currentScale) {
                this.updateZoomScale(value);
            }
        };
        env.panZoom.subscribe(this.panZoomObserver);

        this.planeIntersectionObserver = (point: ReadonlyVec3) => {
            const didMutate = this.updateToOrthoPosition(point);
            if (didMutate) {
                this.updateView();
            } else if (this.environment.isCheckerboardMode) {
                this.updateView(LayerBit.IMAGE_LAYER_BIT);
            }
        };
        env.planeIntersectionPoint.subscribe(this.planeIntersectionObserver);
    }

    /**
     * Method to get all the {@link TextureView.defaultLayers defaultLayers} of a {@link PlaneView}.
     * PlaneView's default Layers include {@link ImageLayer} and {@link ContourLayer}.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public get defaultLayers(): LayerClass<any>[] {
        return [ImageLayer, DoseLayer, ContourLayer];
    }

    /**
     * {@link ReadonlyMat3 Rotation} from ViewSpace to PatientSpace.\
     * Equal to {@link orientation} if {@link additionalRotation} is undefined or identity.\
     * Otherwise {@link orientation} * {@link additionalRotation}.
     */
    public get rotation(): ReadonlyMat3 {
        return this.additionalRotation
            ? mat3.multiply(
                  mat3.create(),
                  this.additionalRotation,
                  this.orientation
              )
            : this.orientation;
    }

    /**
     * {@link ReadonlyMat3 Rotation} from PatientSpace to ViewSpace.\
     * This is the {@link mat3.transpose Transpose} of {@link rotation}.
     */
    public get invRotation(): ReadonlyMat3 {
        return mat3.transpose(mat3.create(), this.rotation);
    }

    /**
     * Method to get the {@link _projectionViewMatrix projectionViewMatrix} of this {@link PlaneView}.\
     * This can be used to convert points directly from PatientSpace to ProjectionSpace.\
     * This is equal to {@link projectionMatrix} * {@link viewMatrix}.
     */
    public get projectionViewMatrix(): ReadonlyMat4 {
        return this._projectionViewMatrix;
    }

    /**
     * Method to get the {@link _viewMatrix viewMatrix} of this {@link PlaneView}.\
     * This can be used to convert points from PatientSpace to ViewSpace.\
     * This is the inverse of {@link transformationMatrix}.
     */
    public get viewMatrix(): ReadonlyMat4 {
        return this._viewMatrix;
    }

    /**
     * Method to clean up resources and event listeners used by this {@link PlaneView}.
     * This will unsubscribe from the {@link environment}'s {@link PanZoom} and {@link PlaneIntersectionPoint} observables.
     */
    public delete(): void {
        super.delete();
        this.geometryLink?.setRole(this, undefined);
        if (this.panZoomObserver) {
            this.environment.panZoom.unsubscribe(this.panZoomObserver);
            this.panZoomObserver = undefined;
        }
        if (this.planeIntersectionObserver) {
            this.environment.planeIntersectionPoint.unsubscribe(
                this.planeIntersectionObserver
            );
            this.planeIntersectionObserver = undefined;
        }
    }

    /**
     * Method to get the {@link _projectionMatrix projectionMatrix} of this {@link PlaneView}.\
     * {@link PlaneView PlaneViews} use orthographic projection and so this matrix is constant.
     *
     * This can be used to convert points from ViewSpace to ProjectionSpace.\
     * However that kind of transformation is rarely needed,
     * it's usually more convenient to just transform directly from PatientSpace to ProjectionSpace.
     */
    public get projectionMatrix(): ReadonlyMat4 {
        return this._projectionMatrix;
    }

    /**
     * Method to get the {@link _transformationMatrix transformationMatrix} of this {@link PlaneView}.\
     * This can be used to convert points from ViewSpace to PatientSpace.\
     * This is the inverse of {@link viewMatrix}.
     */
    public get transformationMatrix(): ReadonlyMat4 {
        return this._transformationMatrix;
    }

    /**
     * Method to get {@link Rectangle3D} presentation of this {@link PlaneView}'s viewport.\
     * Technically this is the same as the {@link transformationMatrix},
     * but {@link Rectangle3D} offers some convenience methods, so providing that kind of object may be useful.
     */
    public get viewportInPatientSpace(): Rectangle3D {
        // TODO REMOVE THIS
        return new Rectangle3D(this._position, this.rotation, this._scale);
    }

    /**
     * The axis-aligned PatientSpace box around the visible view area,
     * extended `halfDepthMm` to either side of the displayed plane. Every
     * corner of the view rectangle is included, so the box covers the
     * visible area however the frame is rotated.
     * @param halfDepthMm Extension to either side of the displayed plane, in mm.
     */
    public getPatientSpaceBoundingBox(halfDepthMm: number = 1): BoundingBox {
        const box = new RotatedBoundingBox(
            new BoundingBox(
                vec3.fromValues(-this._scale[0], -this._scale[1], -halfDepthMm),
                vec3.fromValues(this._scale[0], this._scale[1], halfDepthMm)
            ),
            this.rotation
        ).toAxisAlignedBoundingBox();
        box.translate(this._position);
        return box;
    }

    /**
     * Method to get the resolution of this {@link PlaneView} in mm/pixel
     * (mm of patient space covered by one canvas pixel along each axis).
     */
    public get resolution(): ReadonlyVec2 {
        const resolution = vec2.divide(vec2.create(), this._scale, this.size);
        vec2.scale(resolution, resolution, 2);
        return resolution;
    }

    /**
     * Method to update the {@link position} of this {@link PlaneView}.
     */
    public setViewPosition(newPositionPatientSpace: ReadonlyVec3) {
        this._position = vec3.clone(newPositionPatientSpace);
        this.updateViewMatrix();
    }

    /**
     * Method to move this view to a new position in patient coordinates.
     */
    public panTo(newPositionPatientSpace: ReadonlyVec3) {
        this.moveByUser(() => {
            const delta = vec3.subtract(
                vec3.create(),
                this._position,
                newPositionPatientSpace
            );
            const newPlaneIntersectionPoint = vec3.subtract(
                vec3.create(),
                this.environment.planeIntersectionPoint.coordinates,
                delta
            );
            // Commit both _position and planeIntersectionPoint atomically before
            // dispatching any events so subscribers see consistent state. Callers
            // that read projectionViewMatrix or planeIntersectionPoint during
            // render will get matching values regardless of event order.
            vec3.copy(this._position, newPositionPatientSpace);
            // NOTE: setting .coordinates notifies all PointOfInterest subscribers
            // (including other PlaneViews' planeIntersectionObserver) synchronously
            // before this line returns.
            this.environment.planeIntersectionPoint.coordinates =
                newPlaneIntersectionPoint;
            this.updateViewMatrix();
            this._eventTarget.dispatchEvent(
                new Event('planeIntersectionPointChanged')
            );
            this.updateView();
        });
    }

    /**
     * Get the current center point position of the view in patient coordinates
     */
    public get position(): ReadonlyVec3 {
        return this._position;
    }

    /**
     * The displayed plane: through the view centre ({@link position}) with
     * the view normal ({@link viewDirection}). A snapshot — the view's
     * centre is mutated in place on slice navigation.
     */
    public get plane(): PlaneMm {
        return {
            point: vec3.clone(this._position),
            normal: this.viewDirection,
        };
    }

    /**
     * Method to move this view to align with the plane intersection point.
     */
    public updateToOrthoPosition(newOrthoPosition: ReadonlyVec3): boolean {
        const viewCenter = vec3.transformMat3(
            vec3.create(),
            this._position,
            this.invRotation
        );
        const orthoPointInView = vec3.transformMat3(
            vec3.create(),
            newOrthoPosition,
            this.invRotation
        );
        if (viewCenter[2] !== orthoPointInView[2]) {
            viewCenter[2] = orthoPointInView[2];
            //X, Y and Z are within image space, transform them into patient space
            vec3.transformMat3(this._position, viewCenter, this.rotation);
            this.updateViewMatrix();
            return true;
        }
        return false;
    }

    /**
     * The minimum field of view width in mm to prevent excessive zooming on small structures.
     */
    private readonly _minimumZoomLevel: number = defaultMinimumZoomLevel; // Default 18cm

    /**
     * Method to fit the view around a bounding box.\
     */
    public fitToView(targetBb: RotatedBoundingBox): number {
        // don't zoom too deep in case the structure is very small
        const minViewWidthMm = this._minimumZoomLevel;
        const alignedBb = new BoundingBox();
        alignedBb.expandWithPoints(targetBb.getVertices(this.invRotation));
        const sizeMm = alignedBb.size;
        const scale = vec2.scale(
            vec2.create(),
            vec2.fromValues(
                Math.max(sizeMm[0], minViewWidthMm),
                Math.max(sizeMm[1], minViewWidthMm)
            ),
            0.5
        );
        const resolutions = vec2.divide(vec2.create(), this.size, scale);
        const resolution = Math.min(resolutions[0], resolutions[1]);

        const prevScale = vec2.clone(this._scale);

        vec2.scale(this._scale, this.size, 1 / resolution);

        this.setViewPosition(targetBb.patientSpaceCenter);

        this.updateViewMatrix();

        return this._currentScale / (this._scale[0] / prevScale[0]);
    }

    /**
     * Pans this view so that the centre of `targetBb` is in the middle of the
     * screen. Only the pan changes: the zoom and the displayed slice stay as they are.
     */
    public centerOn(targetBb: RotatedBoundingBox): void {
        const target = vec3.transformMat3(
            vec3.create(),
            targetBb.patientSpaceCenter,
            this.invRotation
        );
        const current = vec3.transformMat3(
            vec3.create(),
            this._position,
            this.invRotation
        );
        // Through-plane is which slice is shown, and this is a pan.
        target[2] = current[2];
        this.setViewPosition(vec3.transformMat3(target, target, this.rotation));
    }

    private _currentScale: number = 1;

    public get currentScale(): number {
        return this._currentScale;
    }

    public resetCurrentScale(scale: number) {
        this._currentScale = scale;
    }

    /**
     * Update the zoom scale of the view.
     * @param newScale The new zoom scale to apply. This is the same as the PanZoom's currentScale.
     */
    public updateZoomScale(newScale: number) {
        vec2.scale(this._scale, this._scale, this._currentScale / newScale);
        this._currentScale = newScale;
        this.updateViewMatrix();
        this.updateView();
        this._eventTarget.dispatchEvent(new Event('zoomScaleChanged'));
    }

    /**
     * Method to zoom to specific coordinates on the view.
     */
    public zoomTo(viewCoords: ReadonlyVec2, newScale: number) {
        this.moveByUser(() => {
            const patientCoords = this.patientCoordsFromCanvasCoords(
                viewCoords[0],
                viewCoords[1]
            );

            // Apply scale without triggering view updates yet
            vec2.scale(this._scale, this._scale, this._currentScale / newScale);
            this._currentScale = newScale;
            this.updateViewMatrix();

            // Move the crosshair to the cursor position before computing
            // the pan delta. updatePlaneIntersectionPoint synchronously
            // notifies observers (including this view's own
            // planeIntersectionObserver → updateToOrthoPosition) which
            // can modify _position. Doing this before the delta
            // calculation ensures the observer side-effects settle first.
            this.updatePlaneIntersectionPoint(patientCoords);

            const newPatientCoords = this.patientCoordsFromCanvasCoords(
                viewCoords[0],
                viewCoords[1]
            );

            const delta = vec3.subtract(
                vec3.create(),
                patientCoords,
                newPatientCoords
            );
            vec3.add(this._position, this._position, delta);
            this.updateViewMatrix();
            this.updateView();
            this._eventTarget.dispatchEvent(new Event('zoomScaleChanged'));
        });
    }

    /**
     * Method to get the plane intersection coordinate value along the view's viewing direction.
     */
    public get planeCoordinates() {
        return vec3.dot(
            this.environment.planeIntersectionPoint.coordinates,
            this.viewDirection
        );
    }

    /**
     * Method to update the {@link _viewMatrix viewMatrix} and {@link _transformationMatrix transformationMatrix} of this {@link PlaneView}.\
     * {@link _transformationMatrix TransformationMatrix} is calculated using {@link mat4.fromRotationTranslationScale}, which takes\
     * {@link rotation}, {@link _position} and {@link scale} as parameters and produces a single {@link mat4 matrix}.
     *
     * What this does under the hood is it sets the {@link this._transformationMatrix transformationMatrix} to identity.\
     * Then translates it by {@link _position}\
     * Rotates by {@link rotation} and lastly\
     * Scales by {@link scale}.
     *
     * After the {@link _transformationMatrix transformationMatrix} has been updated, {@link _viewMatrix viewMatrix} is set to it's {@link mat4.invert inverse}.\
     * And lastly the {@link _projectionViewMatrix projectionViewMatrix} is {@link updateProjectionViewMatrix updated}.
     */
    protected updateViewMatrix(): void {
        mat4.fromRotationTranslationScale(
            this._transformationMatrix,
            quat.fromMat3(quat.create(), this.rotation),
            this._position,
            vec3.fromValues(this._scale[0], this._scale[1], 1)
        );

        mat4.invert(this._viewMatrix, this._transformationMatrix);
        this.updateProjectionViewMatrix();
        this.geometryLink?.viewChanged(this, this.movedByUser);
    }

    /**
     * Runs `change` as a pan or zoom the user makes in this view, which the
     * views linked to it take on ({@link ViewGeometryLink}).
     */
    public moveByUser(change: () => void): void {
        const outer = this.movedByUser;
        this.movedByUser = true;
        try {
            change();
        } finally {
            this.movedByUser = outer;
        }
    }

    /**
     * Shows what `source` shows at the same scale, in mm per pixel, centred
     * on the same point of its plane, whatever the two views' sizes; the
     * slice this view shows stays. `source` faces the way this view faces.
     * Does nothing while either view has no size.
     */
    public takeGeometryOf(source: PlaneView): void {
        const size = this.size;
        const sourceSize = source.size;
        if (
            !(
                size[0] > 0 &&
                size[1] > 0 &&
                sourceSize[0] > 0 &&
                sourceSize[1] > 0
            )
        ) {
            return;
        }
        const scale = vec2.fromValues(
            (source._scale[0] * size[0]) / sourceSize[0],
            (source._scale[1] * size[1]) / sourceSize[1]
        );
        const normal = this.viewDirection;
        const position = vec3.scaleAndAdd(
            vec3.create(),
            source._position,
            normal,
            vec3.dot(
                vec3.subtract(vec3.create(), this._position, source._position),
                normal
            )
        );
        if (
            vec2.equals(scale, this._scale) &&
            vec3.equals(position, this._position) &&
            this._currentScale === source._currentScale
        ) {
            return;
        }
        this._scale = scale;
        this._position = position;
        this._currentScale = source._currentScale;
        this.updateViewMatrix();
        this.updateView();
        this._eventTarget.dispatchEvent(new Event('zoomScaleChanged'));
    }

    /**
     * Method to update the size of this {@link PlaneView}.\
     * @param size The new size of this {@link PlaneView} in pixels.
     * @returns Reference to this {@link PlaneView}.
     */
    public resize(size: vec2): PlaneView {
        const currentAspectRatio = this._scale[0] / this._scale[1];
        const newAspectRatio = size[0] / size[1];
        this._scale[0] *= newAspectRatio / currentAspectRatio;
        this.updateViewMatrix();
        super.resize(size);
        return this;
    }

    /**
     * Private method to update the {@link _projectionViewMatrix projectionViewMatrix} of this {@link PlaneView}.\
     * Calculated as {@link _projectionMatrix projectionMatrix} * {@link _viewMatrix viewMatrix}.
     */
    private updateProjectionViewMatrix(): void {
        mat4.multiply(
            this._projectionViewMatrix,
            this._projectionMatrix,
            this._viewMatrix
        );
    }

    /**
     * Method to get the view direction vector of this PlaneView in PatientSpace.\
     * This is an unit vector in the PlaneView's normal direction. So along the View's z-axis.\
     * Calculated as vec3(0, 0, 1) * {@link rotation}.
     */
    public get viewDirection(): ReadonlyVec3 {
        return vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(0, 0, 1),
            this.rotation
        );
    }

    /**
     * Method to get the view X direction vector of this PlaneView in PatientSpace.\
     */
    public get viewXDirection(): ReadonlyVec3 {
        return vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(1, 0, 0),
            this.rotation
        );
    }

    /**
     * Method to get the view Y direction vector of this PlaneView in PatientSpace.\
     */
    public get viewYDirection(): ReadonlyVec3 {
        return vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(0, 1, 0),
            this.rotation
        );
    }

    /**
     * Method to get the canvas coordinates {@link ReadonlyVec2 vector} of a Patient space point.\
     * @param point
     * @returns
     */
    public canvasCoordsFromPatientCoords(point: ReadonlyVec3): ReadonlyVec2 {
        return projectPatientToCanvasPx(
            point,
            this.projectionViewMatrix,
            this.size
        );
    }

    protected override captureFrameCamera(): FrameCamera {
        return {
            projectionViewMatrix: mat4.clone(this._projectionViewMatrix),
            viewDirection: this.viewDirection,
            viewXDirection: this.viewXDirection,
            planeIntersectionPointMm: vec3.clone(
                this.environment.planeIntersectionPoint.coordinates
            ),
        };
    }

    /**
     * Method to get the PatientSpace coordinates {@link ReadonlyVec3 vector} of a point on the canvas.\
     * @param canvasX
     * @param canvasY
     * @returns
     */
    public patientCoordsFromCanvasCoords(
        canvasX: number,
        canvasY: number
    ): vec3 {
        const coordinates = vec2.fromValues(
            canvasX / this.size[0],
            1 - canvasY / this.size[1]
        );
        coordinates[0] = coordinates[0] * 2 - 1;
        coordinates[1] = coordinates[1] * 2 - 1;
        const inv = mat4.invert(mat4.create(), this.projectionViewMatrix);
        const result = vec3.transformMat4(
            vec3.create(),
            vec3.fromValues(coordinates[0], coordinates[1], 0),
            inv
        );
        return result;
    }

    /**
     * Moves the {@link PlaneView.planeIntersectionPoint planeIntersectionPoint}
     * this view shares with every other view of its environment.
     */
    public updatePlaneIntersectionPoint(point: ReadonlyVec3) {
        this.environment.updatePlaneIntersectionPoint(point);
    }

    /**
     * The image grid axis this view looks along, or undefined when the
     * displayed plane coincides with no image slice plane: no image is
     * loaded, the frame is rotated, or the cut runs across an obliquely
     * acquired grid. Which axis it is follows the acquisition, not the
     * view's anatomical label — on a coronal acquisition the transversal
     * view looks along grid Y.
     */
    public get imageSliceAxis(): SliceAxis | undefined {
        return this.environment.image?.grid.axisAlong(this.viewDirection);
    }

    /**
     * Snap the plane intersection point through-plane to the nearest image
     * slice center, when the displayed plane coincides with an image slice
     * plane ({@link imageSliceAxis}). When it does not — sagittal or
     * coronal cuts of an obliquely acquired grid, or any rotated frame —
     * there is no slice center to snap to and this is a no-op: the drawing
     * slab is then centered on the plane intersection point as-is.
     */
    public snapToImagePixel() {
        const image = this.environment.image;
        const axis = this.imageSliceAxis;
        if (!image || axis === undefined) {
            return;
        }
        const curIntersectionPoint =
            this.environment.planeIntersectionPoint.coordinates;
        const snappedPoint = image.grid.snapPositionToSliceCenter(
            curIntersectionPoint,
            axis
        );
        if (vec3.distance(snappedPoint, curIntersectionPoint) > 1e-4) {
            this.updatePlaneIntersectionPoint(snappedPoint);
        }
    }

    /**
     * Method to get the canvas coordinates {@link ReadonlyVec2 vector} of a point in view space.
     **/
    public scrollLevelToPlaneIntersectionPoint(
        scrollType: string,
        delta: number
    ): void {
        const canvasHeight = this.canvas?.getBoundingClientRect().height;
        const canvasWidth = this.canvas?.getBoundingClientRect().width;
        if (!canvasHeight || !canvasWidth) {
            return;
        }
        const currentScrollLevel = this.planeIntersectionPointToScrollLevel();
        const newScrollLevel = {
            ...currentScrollLevel,
            [scrollType]: Math.min(
                Math.max(
                    scrollType === 'horizontal'
                        ? currentScrollLevel.horizontal +
                              (delta / canvasHeight) * 100
                        : currentScrollLevel.vertical +
                              (delta / canvasWidth) * 100,
                    0
                ),
                100
            ),
        };
        const offsetX = canvasWidth * (newScrollLevel.vertical / 100);
        const offsetY = canvasHeight * (newScrollLevel.horizontal / 100);
        const canvasCoords = vec2.fromValues(offsetX, offsetY);
        const newOrthoPoint = this.patientCoordsFromCanvasCoords(
            canvasCoords[0],
            canvasCoords[1]
        );
        this.updatePlaneIntersectionPoint(newOrthoPoint);
        this.updateView();
    }

    /**
     * Method to get the scroll level of the plane intersection point.\
     * @returns The scroll level of the plane intersection point as a percentage of the canvas size.\
     *
     **/
    public planeIntersectionPointToScrollLevel(): {
        vertical: number;
        horizontal: number;
    } {
        const orthoPoint = this.planeIntersectionPoint.coordinates;
        const canvasCoords = this.canvasCoordsFromPatientCoords(orthoPoint);
        const clientHeight = this.canvas?.getBoundingClientRect().height;
        const clientWidth = this.canvas?.getBoundingClientRect().width;
        if (clientHeight && clientWidth) {
            const vertical = (canvasCoords[0] / clientWidth) * 100;
            const horizontal = (canvasCoords[1] / clientHeight) * 100;
            return { vertical, horizontal };
        }
        return { vertical: 0, horizontal: 0 };
    }

    /* Method to convert {@link ReadonlyVec2 clientCoordinates} in between [0, pixelSize] to {@link ReadonlyVec3 PatientCoordinates}.\
     * This is done by first converting the {@link ReadonlyVec2 clientCoordinates} to {@link ReadonlyVec2 viewCoordinates} using {@link clientToViewCoordinates}.\
     * Then the {@link ReadonlyVec2 viewCoordinates} are converted to {@link ReadonlyVec3 PatientCoordinates} by {@link vec3.transformMat4 transforming} with {@link mat4.invert inverse} of {@link projectionViewMatrix}.\
     * The Z coordinate used for the conversion is set to 0, so the {@link ReadonlyVec3 PatientCoordinates} are on the view plane.
     * @param clientCoordinates {@link ReadonlyVec2 ClientCoordinates} in between [0, pixelSize].
     * @returns The {@link ReadonlyVec3 PatientCoordinates} at the coordinates specified by {@link clientCoordinates}.
     */
    public clientToPatientCoordinates(clientCoordinates: ReadonlyVec2): vec3 {
        const view = this.clientToViewCoordinates(clientCoordinates);
        const inv = mat4.invert(mat4.create(), this.projectionViewMatrix);
        const result = vec3.transformMat4(
            vec3.create(),
            vec3.fromValues(view[0], view[1], 0),
            inv
        );
        return result;
    }

    public setListener(listener: PlaneViewMouseListener): PlaneView {
        this.listener = listener;
        return this;
    }
}

export interface PlaneViewMouseListener {
    onMouseMoved(planeView: PlaneView, mouseData: MouseData): void;
}
